import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";
import { OfferModal } from "tsi-common-react/src/components/OfferModal/index";
import { useLazyBackground } from "tsi-common-react/src/utils/lazyBackground";

import type { HeroImageBlock as Value } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

type Props = Immutable<{
    value: Value;
}>;

// --- Tracking (port of tsicommon_tracker_tags.py for this block) -------------
//
// The legacy template wrapped this block in two Django tags:
//   {% select_promotion_tracking_attrs value=value
//        block_name='hero-image-block' fallback_name='Hero Image' %}
//   {% track_view_promotion value=value
//        block_name='hero-image-block' fallback_name='Hero Image' %}
//
// Phase 2 removes that server wrapper entirely, so both the `view_promotion`
// (fire-on-mount) and `select_promotion` (CTA click) GA4 events must be
// reproduced here. The payloads below are byte-for-byte ports of the Python
// `_build_promotion_event` / `_build_promotion_metadata` helpers.

const BLOCK_NAME = "hero-image-block";
const FALLBACK_NAME = "Hero Image";

// `block_index` resolves to `None` for this block: the StructValue has no `id`
// attribute and the surrounding `self` block id is also unset (true in
// production, not just in the gate fixture). The Python helpers therefore:
//   - build `creative_slot` as `f"{block_name}-{block_index or 1}"`, so the
//     index falls back to 1 → "hero-image-block-1".
//   - append the `-{block_index}` suffix to `promotion_id` ONLY when
//     `block_index is not None` → so the id carries NO numeric suffix.
const CREATIVE_SLOT = `${BLOCK_NAME}-1`;

const stripHtml = (html: string): string => {
    if (!html) {
        return "";
    }
    const text = html.replace(/<[^>]*>/g, " ");
    return text.replace(/\s+/g, " ").trim();
};

// Mirror of django.utils.text.slugify (allow_unicode=False).
const slugify = (value: string): string =>
    value
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");

/**
 * Port of `_slugify_tracking_candidates`. For each candidate: strip HTML tags
 * (mirroring `_extract_text_from_richtext` → `strip_tags`, since header_text is
 * raw `|safe` HTML), then replace URL separators `[:/.]` with spaces so they
 * become hyphens rather than being stripped, then slugify. Drop empties and
 * duplicates, then join with "-". The per-candidate strip is what makes the
 * dedup work: a header_text of "<span>Sale</span>" reduces to "sale", matching
 * the already-stripped promotion_name and collapsing away — so a plain header
 * yields just the name, while a real OfferDetailPage adds its url slug.
 */
const slugifyTrackingCandidates = (
    candidates: readonly string[],
    fallback: string,
): string => {
    const parts: string[] = [];
    const seen = new Set<string>();
    for (const candidate of candidates) {
        const normalized = stripHtml(candidate).replace(/[:/.]/g, " ");
        const slug = slugify(normalized);
        if (!slug || seen.has(slug)) {
            continue;
        }
        parts.push(slug);
        seen.add(slug);
    }
    return parts.join("-") || fallback;
};

type PromotionEvent = {
    event: "view_promotion" | "select_promotion";
    creative_name: string;
    creative_slot: string;
    promotion_id: string;
    promotion_name: string;
    items: readonly never[];
};

const buildPromotionEvent = (
    value: Immutable<Value>,
    eventName: "view_promotion" | "select_promotion",
): PromotionEvent => {
    // `_normalize_tracking_name_id`: first non-empty name candidate wins; for a
    // HeroImageBlock the only populated candidate is `header_text` (the other
    // candidates — header_content/title/heading/name/content/pre_header_text/
    // cta_link/linked_offer/linked_page — don't exist on this block). Falls
    // back to `fallback_name` ("Hero Image") when header_text is empty.
    const headerText = stripHtml(value.header_text ?? "");
    const promotionName = headerText || FALLBACK_NAME;

    // `_build_promotion_metadata`: promotion_id is the slugified join of
    // [promotion_name, ...extra_id_candidates]. For this block the only
    // populated id candidates are header_text and page.url (header_content/
    // cta_link/linked_offer/link don't exist here). page.url folds in via the
    // `[:/.]`→space replacement (e.g. "/offers/foo/" → "offers-foo").
    const promotionId = slugifyTrackingCandidates(
        [promotionName, value.header_text ?? "", value.page?.url ?? ""],
        slugify(promotionName) || slugify(BLOCK_NAME) || "tracking-block",
    );

    return {
        event: eventName,
        creative_name: promotionName,
        creative_slot: CREATIVE_SLOT,
        promotion_id: promotionId,
        promotion_name: promotionName,
        // `_build_promotion_event` always emits an `items` array (empty when
        // there are no product entries, as for this block) after _compact_dict.
        items: [],
    };
};

const isDoNotTrackEnabled = (win: Window): boolean => {
    const nav = win.navigator as Navigator & {
        msDoNotTrack?: string;
        globalPrivacyControl?: boolean;
    };
    return (
        nav.doNotTrack === "1" ||
        nav.msDoNotTrack === "1" ||
        nav.globalPrivacyControl === true
    );
};

const pushPromotionEvent = (
    value: Immutable<Value>,
    eventName: "view_promotion" | "select_promotion",
): void => {
    const win = getWindow();
    if (!win) {
        return;
    }
    // Mirror the Django tags' DNT guard (`request.headers["DNT"] == "1"`).
    if (isDoNotTrackEnabled(win)) {
        return;
    }
    // Mirror the legacy push_to_datalayer.html flush + push.
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({ items: null });
    win.dataLayer.push(buildPromotionEvent(value, eventName));
};

/**
 * Mirror PromotionBlock's visibility gate (and the Django
 * push_to_datalayer.html `_isVisible` check): suppress `view_promotion` when
 * the block is hidden (e.g. an ancestor with `display: none`), so GA4
 * impressions aren't inflated. Prefers the native `checkVisibility()` API;
 * falls back to a computed-style ancestor walk.
 */
const isElementVisible = (el: HTMLElement | null, win: Window): boolean => {
    if (!el) {
        return true;
    }
    const elWithCheck = el as HTMLElement & {
        checkVisibility?: () => boolean;
    };
    if (typeof elWithCheck.checkVisibility === "function") {
        return elWithCheck.checkVisibility();
    }
    let current: HTMLElement | null = el;
    const root = win.document.documentElement;
    while (current && current !== root) {
        if (win.getComputedStyle(current).display === "none") {
            return false;
        }
        current = current.parentElement;
    }
    return true;
};

// --- Component ---------------------------------------------------------------

export const HeroImageBlock = (props: Props): React.JSX.Element | null => {
    const v = props.value;

    const hasFiredView = useRef(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        // Fire `view_promotion` once on mount, mirroring the server-rendered
        // {% track_view_promotion %} impression (which the fragment generator
        // strips, so this event is NOT covered by the styleguide gate).
        if (hasFiredView.current) {
            return;
        }
        hasFiredView.current = true;
        // The promo gate below short-circuits render to null; in that case the
        // effect still runs, so re-check the gate before emitting the view.
        if (v.promo_period && !v.promo_period.is_promo_active) {
            return;
        }
        // Gate on visibility like {% track_view_promotion %} / PromotionBlock:
        // a hidden block (e.g. ancestor `display: none`) must not emit an
        // impression.
        const win = getWindow();
        if (!win || !isElementVisible(rootRef.current, win)) {
            return;
        }
        pushPromotionEvent(v, "view_promotion");
        // Fire-on-mount only: intentionally empty deps. exhaustive-deps is not
        // enabled in this codebase, so a disable annotation would be flagged.
    }, []);

    // Defer the block's CSS background image until it scrolls into view (eager
    // under puppeteer/SSR) via the shared per-element hook. Reuses `rootRef`,
    // already attached to the `.hero-image-block.lazy-background` element.
    useLazyBackground(rootRef);

    // Promo gate: reproduce `{% if not value.promo_period or
    // value.promo_period.is_promo_active %}`.
    if (v.promo_period && !v.promo_period.is_promo_active) {
        return null;
    }

    return (
        <div
            ref={rootRef}
            className="hero-image-block lazy-background"
            // Legacy used `self.image.file.url` (the raw original file). The
            // serialized `image.url` is that same original image URL.
            data-background-image={v.image?.url ?? ""}
        >
            <div className="hero-image-block__overlay">
                {v.pre_header_text_desktop && (
                    // The serialized RichTextBlock value is already the
                    // server-rendered `<div class="rich-text">…</div>` HTML
                    // (ClientRenderingBarrier → block.render()). Inject it
                    // directly: wrapping it in <RichTextBlock> would add an
                    // extra <div> not present in the golden markup.
                    <div
                        className="hero-image-block__preheader--desktop"
                        dangerouslySetInnerHTML={{
                            __html: v.pre_header_text_desktop,
                        }}
                    />
                )}
                {v.pre_header_text_mobile && (
                    <div
                        className="hero-image-block__preheader--mobile"
                        dangerouslySetInnerHTML={{
                            __html: v.pre_header_text_mobile,
                        }}
                    />
                )}
                {/* Legacy `{{ self.header_text | safe }}`: header_text is a
                    CharBlock string rendered as raw HTML. */}
                <h1 dangerouslySetInnerHTML={{ __html: v.header_text ?? "" }} />
                {/* Legacy `{{ self.description }}`: plain (escaped) text. */}
                <p>{v.description}</p>
            </div>
            {/* Legacy: this wrapping <div> carried the
                {% select_promotion_tracking_attrs %} `onclick=""` that pushed
                the `select_promotion` event. Reproduce it as a React onClick so
                clicking the CTA (or anywhere in the wrapper) fires the event. */}
            <div onClick={() => pushPromotionEvent(v, "select_promotion")}>
                <OfferModal
                    offerDetailURL={v.page?.url ?? ""}
                    // Legacy passed `cta_text=self.cta_text|richtext`. cta_text
                    // is a bare CharBlock string; the `|richtext` filter wrapped
                    // it in `<div class="rich-text">`. OfferModal takes ctaText
                    // as a string and dangerouslySetInnerHTML's it, so hand-build
                    // the same wrapper here.
                    ctaText={`<div class="rich-text">${v.cta_text ?? ""}</div>`}
                    ctaArrow={false}
                    isButton={true}
                />
            </div>
        </div>
    );
};
